import { useAppSelector } from '../../redux/hooks';
import { Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import StyledWrapper from './style';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import { getLocalAuth } from '../../utils/environmentUtils';
import { useDeleteApplicationMutation } from '../../services/endpoints/applicationEndpoints';
import usePermissions from '../../hooks/usePermissions';

const ApplicationDeleteModal = ({
	isOpen,
	handleClose,
	projectTitle,
	handleAlert,
	handleApplicationListAlert,
	setJustDeletedApplication
}) => {
	const accountSource = useAppSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const navigate = useNavigate();

	let { programID } = useParams();
	programID = programID?.replace('p-', '');
	const { hasEditPermissions } = usePermissions();
	const isGrantor = hasEditPermissions;
	const [searchParams] = useSearchParams();

	const isPortal = searchParams?.get('iFrame') === 'true';
	const redirectPath = `/programs/p-${programID}/applications${isPortal ? '?iFrame=true' : ''}`;

	const path = window.location.pathname;
	const pathSegments = path.split('/');
	const appID = pathSegments.find((segment) => segment.includes('api-'))?.replace('api-', '');

	const grantorWarningMessage = (
		<>
			Are you sure you want to delete this application submission?
			<br />
			Please note that once this submission has been deleted, it cannot be restored.
			<br />
			If you are looking to reject this application, do not continue with deletion and use the
			‘Decline’ functionality to decline the application instead. You can decline an application
			once it has progressed through all submission and evaluation stages.
		</>
	);

	const ownerWarningMessage = `Are you sure you want to delete this application? Please note that once this application has been deleted, it cannot be restored.`;
	const warningMessage = isGrantor ? grantorWarningMessage : ownerWarningMessage;
	const [deleteApplication, { isLoading: isDeleting }] = useDeleteApplicationMutation();
	const handleDeleteApplication = () => {
		const endpointOptions = {
			apiToken: apiToken,
			uniqueIdentifier: appID
		};

		deleteApplication(endpointOptions)
			.unwrap()
			.then((response) => {
				const successDeleteMessage = `You successfully deleted the application: ${
					projectTitle || ''
				} [api-${appID}].`;
				navigate(redirectPath, { replace: true });

				handleApplicationListAlert(successDeleteMessage, 'success', false);
				setJustDeletedApplication(true);
			})
			.catch((error) => {
				handleAlert('Failed to delete the application', 'error');
				handleClose();
			});
	};

	return (
		<StyledWrapper.CustomDialog
			fullWidth
			maxWidth="md"
			open={isOpen}
			onClose={handleClose}
			sx={{
				'& .MuiDialog-paper': {
					maxHeight: '90vh',
					overflow: 'hidden'
				}
			}}
			aria-labelledby="responsive-dialog-title"
		>
			<StyledWrapper.CustomDialogTitle id="responsive-dialog-title">
				<WarningIcon color={'warning'} />
				Warning: This action is irreversible!
			</StyledWrapper.CustomDialogTitle>
			<StyledWrapper.CustomDialogContent>{warningMessage}</StyledWrapper.CustomDialogContent>

			<StyledWrapper.CustomDialogActions style={{ justifyContent: 'center' }}>
				<Button size="large" variant="outlined" onClick={handleClose} color="warning" autoFocus>
					Cancel
				</Button>
				<LoadingButton
					size="large"
					variant="contained"
					autoFocus
					loading={isDeleting}
					onClick={handleDeleteApplication}
				>
					Delete Application
				</LoadingButton>
			</StyledWrapper.CustomDialogActions>
		</StyledWrapper.CustomDialog>
	);
};

export default ApplicationDeleteModal;
